import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
} from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout/index.js";
import { ArrowBack } from "@mui/icons-material";
import MaterialTypography from "../../../components/MaterialTypography/index.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import MaterialBox from "../../../components/MaterialBox/index.js";
import { useEffect, useState } from "react";
import { fetchDataById } from "../../../services/index.js";
import MaterialButton from "../../../components/MaterialButton/index.js";
import { useSnackbar } from "../../../context/popup.js";
import { setCircleLoading } from "../../../components/Loading/index.js";

const TransformFunctionDictionaryView = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const transformId = searchParams.get("id");
  const pageType = searchParams.get("pageType");
  const pageMode =
    pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    category: "",
    sampleInput: "",
    sampleOutput: "",
    configSchema: "",
    isActive: false,
    createdAt: "",
    updatedAt: "",
  });

  const toDateInputValue = (dateStr) => {
    if (!dateStr || dateStr === true) return "";

    const date = new Date(Date.parse(dateStr));
    if (isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  useEffect(() => {
    if (pageMode === "add" || !transformId) return;

    const fetchTransformById = async () => {
      setCircleLoading(true);
      try {
        const response = await fetchDataById(
          `/api-engine/transform-dictionary/${transformId}`
        );

        const responseData = response?.data;

        if (response?.success && responseData) {
          setFormData({
            code: responseData.code || "",
            name: responseData.name || "",
            category: responseData.category || "",
            sampleInput: responseData.sampleInput || "—",
            sampleOutput: responseData.sampleOutput || "—",
            configSchema: responseData.configSchema
              ? JSON.stringify(responseData.configSchema, null, 2)
              : "—",
            isActive: responseData.isActive,
            createdAt: toDateInputValue(responseData.createdAt) || "",
            updatedAt: toDateInputValue(responseData.updatedAt) || "",
          });
        }
      } catch (error) {
        console.error("Error fetching Transform Dictionary:", error);
        showSnackbar(error?.response?.data?.message || "Error fetching Transform Dictionary","error");
      } finally {
        setCircleLoading(false);
      }
    };

    fetchTransformById();
  }, [pageMode, transformId]);

  const InfoCard = ({ label, value }) => (
    <MaterialBox
      p={2.5}
      borderRadius={3}
      boxShadow="0 6px 20px rgba(0,0,0,0.06)"
      bgcolor="background.paper"
    >
      <MaterialTypography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {label}
      </MaterialTypography>
      <MaterialTypography variant="h6" fontWeight="medium" mt={0.5}>
        {value || "—"}
      </MaterialTypography>
    </MaterialBox>
  );

  const StatusPill = ({ active }) => (
    <MaterialBox
      px={2}
      py={0.5}
      borderRadius={20}
      bgcolor={active ? "success.light" : "error.light"}
      color={active ? "success.dark" : "error.dark"}
      fontSize={13}
      fontWeight={600}
    >
      {active ? "ACTIVE" : "INACTIVE"}
    </MaterialBox>
  );

  return (
    <DashboardLayout>
      <Card sx={{ minHeight: 500 }}>
        <CardHeader
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={() => navigate(-1)}>
                  <ArrowBack />
                </IconButton>
                <Stack direction="column" spacing={0}>
                  <MaterialTypography variant="h5" fontWeight="medium">
                    Transform Function Dictionary Details
                  </MaterialTypography>
                </Stack>
              </Stack>
            </Stack>
          }
        />
        <CardContent>
          <Stack spacing={4}>
            {/* 🔹 Top Highlight Section */}
            <MaterialBox
              p={3}
              borderRadius={4}
              sx={{
                background:
                  "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(236,72,153,0.12))",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
              >
                <Stack spacing={0.5}>
                  <MaterialTypography variant="h4" fontWeight="bold">
                    {formData.name}
                  </MaterialTypography>
                  <MaterialTypography color="text.secondary">
                    Code: <b>{formData.code}</b> | Category:{" "}
                    <b>{formData.category}</b>
                  </MaterialTypography>
                </Stack>
                <StatusPill active={formData.isActive} />
              </Stack>
            </MaterialBox>

            {/* 🔹 Info Cards */}
            <MaterialBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }}
              gap={3}
            >
              <InfoCard label="Name" value={formData.name} />
              <InfoCard label="Code" value={formData.code} />
              <InfoCard label="Category" value={formData.category} />
              <InfoCard
                label="Status"
                value={formData.isActive ? "Active" : "Inactive"}
              />
            </MaterialBox>

            {/* 🔹 Sample Input / Output */}
            <MaterialBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }}
              gap={3}
            >
              <InfoCard label="Sample Input" value={formData.sampleInput} />
              <InfoCard label="Sample Output" value={formData.sampleOutput} />
            </MaterialBox>

            {/* 🔹 Config Schema (Dark Code Panel) */}
            <MaterialBox>
              <MaterialTypography
                variant="subtitle1"
                fontWeight="bold"
                mb={1}
              >
                Configuration Schema
              </MaterialTypography>

              <MaterialBox
                component="pre"
                p={3}
                borderRadius={3}
                sx={{
                  background: "#0f172a",
                  color: "#e5e7eb",
                  fontSize: 13,
                  overflowX: "auto",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
                }}
              >
                {formData.configSchema}
              </MaterialBox>
            </MaterialBox>

            {/* 🔹 Timeline / Metadata */}
            <MaterialBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }}
              gap={3}
            >
              <InfoCard label="Created At" value={formData.createdAt} />
              <InfoCard label="Updated At" value={formData.updatedAt} />
            </MaterialBox>
          </Stack>
        </CardContent>

        <MaterialBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {pageMode !== "view" && (
            <>
              <MaterialButton
                sx={{ m: 2 }}
                variant="outlined"
                color="dark"
                onClick={() => navigate(-1)}
              >
                Back
              </MaterialButton>
            </>
          )}
        </MaterialBox>
      </Card>
    </DashboardLayout>
  );
};

export default TransformFunctionDictionaryView;
